import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface BrandProductCategory {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface BrandProductSubcategory {
  id: string;
  brand_id: string;
  category_id: string;
  name: string;
  color: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface BrandProductHookEnhanced {
  id: string;
  brand_id: string;
  user_id: string;
  product: string;
  description?: string;
  type: 'Product' | 'Service' | 'Hook';
  funnel_stage: 'TofU' | 'MofU' | 'BofU' | 'Conversion';
  landing_page_link?: string;
  category_id?: string;
  subcategory_id?: string;
  tags: string[];
  more_tags: string[];
  tag3: string[];
  price?: number;
  status: 'active' | 'inactive' | 'discontinued';
  images?: string[];
  created_at: string;
  updated_at: string;
}

export const useBrandProductsEnhanced = (brandId: string) => {
  const queryClient = useQueryClient();

  // Fetch products
  const {
    data: products,
    isLoading: productsLoading,
    error: productsError
  } = useQuery({
    queryKey: ['brand-products-enhanced', brandId],
    queryFn: async (): Promise<BrandProductHookEnhanced[]> => {
      const { data, error } = await supabase
        .from('brand_products_hooks')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(item => {
        // Parse tags array
        const parseJsonbArray = (value: any): string[] => {
          if (Array.isArray(value)) return value.map(String);
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              return Array.isArray(parsed) ? parsed.map(String) : [];
            } catch {
              return [];
            }
          }
          return [];
        };
        
        const parsedTags = parseJsonbArray(item.tags);
        
        
        return {
          ...item,
          type: item.type as 'Product' | 'Service' | 'Hook',
          funnel_stage: item.funnel_stage as 'TofU' | 'MofU' | 'BofU' | 'Conversion',
          category_id: item.category_id || undefined,
          subcategory_id: item.subcategory_id || undefined,
          tags: parsedTags,
          more_tags: parseJsonbArray(item.more_tags),
          tag3: parseJsonbArray(item.tag3),
          images: parseJsonbArray(item.images),
          status: (item.status as 'active' | 'inactive' | 'discontinued') || 'active'
        };
      });
    },
    enabled: !!brandId,
  });

  // Fetch categories
  const {
    data: categories,
    isLoading: categoriesLoading
  } = useQuery({
    queryKey: ['brand-product-categories', brandId],
    queryFn: async (): Promise<BrandProductCategory[]> => {
      const { data, error } = await supabase
        .from('brand_product_categories')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  // Fetch subcategories
  const {
    data: subcategories,
    isLoading: subcategoriesLoading
  } = useQuery({
    queryKey: ['brand-product-subcategories', brandId],
    queryFn: async (): Promise<BrandProductSubcategory[]> => {
      const { data, error } = await supabase
        .from('brand_product_subcategories')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (categoryData: { name: string; color: string; description?: string }) => {
      const { data, error } = await supabase
        .from('brand_product_categories')
        .insert({
          brand_id: brandId,
          ...categoryData
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-categories', brandId] });
      toast({
        title: "Success",
        description: "Category created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create category",
        variant: "destructive",
      });
    }
  });

  // Create subcategory mutation
  const createSubcategoryMutation = useMutation({
    mutationFn: async (subcategoryData: { 
      category_id: string; 
      name: string; 
      color: string; 
      description?: string 
    }) => {
      const { data, error } = await supabase
        .from('brand_product_subcategories')
        .insert({
          brand_id: brandId,
          ...subcategoryData
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-subcategories', brandId] });
      toast({
        title: "Success",
        description: "Subcategory created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create subcategory",
        variant: "destructive",
      });
    }
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; color?: string; description?: string }) => {
      const { data, error } = await supabase
        .from('brand_product_categories')
        .update(updates)
        .eq('id', id)
        .eq('brand_id', brandId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-categories', brandId] });
      toast({
        title: "Success",
        description: "Category updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive",
      });
    }
  });

  // Update subcategory mutation
  const updateSubcategoryMutation = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; color?: string; description?: string; category_id?: string }) => {
      const { data, error } = await supabase
        .from('brand_product_subcategories')
        .update(updates)
        .eq('id', id)
        .eq('brand_id', brandId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-subcategories', brandId] });
      toast({
        title: "Success",
        description: "Subcategory updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update subcategory",
        variant: "destructive",
      });
    }
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      // Check if there are any subcategories using this category
      const { data: subcategoriesInCategory, error: subcategoriesError } = await supabase
        .from('brand_product_subcategories')
        .select('id')
        .eq('category_id', categoryId);

      if (subcategoriesError) throw subcategoriesError;

      if (subcategoriesInCategory && subcategoriesInCategory.length > 0) {
        throw new Error('Cannot delete category: there are subcategories connected to it');
      }

      const { error } = await supabase
        .from('brand_product_categories')
        .delete()
        .eq('id', categoryId)
        .eq('brand_id', brandId);

      if (error) throw error;
      return categoryId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-categories', brandId] });
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Delete subcategory mutation
  const deleteSubcategoryMutation = useMutation({
    mutationFn: async (subcategoryId: string) => {
      // First, unassign all products from this subcategory
      const { error: unassignError } = await supabase
        .from('brand_products_hooks')
        .update({ subcategory_id: null })
        .eq('subcategory_id', subcategoryId)
        .eq('brand_id', brandId);

      if (unassignError) throw unassignError;

      // Then delete the subcategory
      const { error } = await supabase
        .from('brand_product_subcategories')
        .delete()
        .eq('id', subcategoryId)
        .eq('brand_id', brandId);

      if (error) throw error;
      return subcategoryId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-subcategories', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-products-enhanced', brandId] });
      toast({
        title: "Success",
        description: "Subcategory deleted and products unassigned successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Bulk import mutation
  const bulkImportMutation = useMutation({
    mutationFn: async (importData: { 
      products: any[]; 
      createMissingCategories?: boolean 
    }) => {
      const { data, error } = await supabase.functions.invoke('brand-products-bulk-import', {
        body: {
          brandId,
          products: importData.products,
          createMissingCategories: importData.createMissingCategories
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['brand-products-enhanced', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-product-categories', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-product-subcategories', brandId] });
      
      toast({
        title: "Import Complete",
        description: `${result.created} products imported, ${result.skipped} skipped, ${result.errors.length} errors`,
      });
    },
    onError: (error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Helper functions to check if deletion is safe
  const canDeleteCategory = (categoryId: string) => {
    return !subcategories?.some(sub => sub.category_id === categoryId);
  };

  const canDeleteSubcategory = (subcategoryId: string) => {
    return true; // Always allow subcategory deletion (will unassign products first)
  };

  return {
    products,
    categories,
    subcategories,
    isLoading: productsLoading || categoriesLoading || subcategoriesLoading,
    error: productsError,
    createCategory: createCategoryMutation.mutate,
    createSubcategory: createSubcategoryMutation.mutate,
    updateCategory: updateCategoryMutation.mutate,
    updateSubcategory: updateSubcategoryMutation.mutate,
    deleteCategory: deleteCategoryMutation.mutate,
    deleteSubcategory: deleteSubcategoryMutation.mutate,
    bulkImport: bulkImportMutation.mutate,
    isCreatingCategory: createCategoryMutation.isPending,
    isCreatingSubcategory: createSubcategoryMutation.isPending,
    isUpdatingCategory: updateCategoryMutation.isPending,
    isUpdatingSubcategory: updateSubcategoryMutation.isPending,
    isDeletingCategory: deleteCategoryMutation.isPending,
    isDeletingSubcategory: deleteSubcategoryMutation.isPending,
    isImporting: bulkImportMutation.isPending,
    importResult: bulkImportMutation.data,
    canDeleteCategory,
    canDeleteSubcategory
  };
};